import type { CustomerOrderDetails, OrderStage } from '../types';

/**
 * Крупные вехи заказа глазами заказчика. Этапов у заказа пятнадцать — это
 * рабочая кухня художника, и заказчику она ничего не объясняет. Здесь пять
 * точек, каждая отвечает на вопрос «что уже случилось и чего ждут от меня».
 *
 * Считается из уже имеющихся полей, ничего не хранится: веха — это взгляд на
 * состояние заказа, а не отдельная сущность, которую надо поддерживать в
 * согласии с этапами.
 */
export type MilestoneState = 'done' | 'current' | 'todo';

export interface Milestone {
  key: 'agreed' | 'deposit' | 'sketch' | 'work' | 'files';
  label: string;
  state: MilestoneState;
}

/** Этапы, после которых скетч считается пройденным. */
const AFTER_SKETCH: OrderStage[] = [
  'LINEART',
  'COLORING',
  'DETAILING',
  'WAITING_REVISIONS',
  'REVISION',
  'FINAL_REVIEW',
  'READY',
  'DELIVERED',
  'COMPLETED',
];

/** Этапы, после которых работа сдана и остаётся только получить файлы. */
const AFTER_WORK: OrderStage[] = ['READY', 'DELIVERED', 'COMPLETED'];

export function orderMilestones(order: CustomerOrderDetails): Milestone[] {
  const paid = order.showPrice && order.payment ? Number(order.payment.paidAmount) > 0 : null;

  const done = {
    // Срок согласован — значит договорились обо всём остальном тоже.
    agreed: order.deadlineAgreedAt !== null,
    /*
     * Цены может не быть видно: художник вправе скрыть суммы. Тогда веху не
     * выдумываем, а считаем пройденной вместе с согласованием — врать про
     * деньги хуже, чем не показывать их вовсе.
     */
    deposit: paid === null ? order.deadlineAgreedAt !== null : paid,
    sketch: AFTER_SKETCH.includes(order.currentStage),
    work: AFTER_WORK.includes(order.currentStage),
    files: order.currentStage === 'COMPLETED' || order.deliveryConfirmedAt !== null,
  };

  const labels: Record<Milestone['key'], string> = {
    agreed: 'Договорились',
    deposit: 'Предоплата',
    sketch: 'Скетч',
    work: 'Работа',
    files: 'Файлы',
  };

  const keys: Milestone['key'][] = ['agreed', 'deposit', 'sketch', 'work', 'files'];
  // Текущая веха — первая непройденная: именно на ней сейчас стоит заказ.
  const currentIndex = keys.findIndex((key) => !done[key]);

  return keys.map((key, index) => ({
    key,
    label: labels[key],
    state: done[key] ? 'done' : index === currentIndex ? 'current' : 'todo',
  }));
}

/**
 * Сколько бесплатных правок осталось. Показывается до того, как заказчик
 * попросит правку, а не после: счётчик, увиденный вовремя, удерживает объём
 * работы честным, а увиденный постфактум — просто расстраивает.
 */
export function revisionsLeft(order: Pick<CustomerOrderDetails, 'revisionLimit' | 'revisionUsed'>) {
  const left = Math.max(0, order.revisionLimit - order.revisionUsed);

  return { left, limit: order.revisionLimit, exhausted: left === 0 };
}
